/**
 * Animated stage scenes — a short storyboard that cuts between separate
 * scenes as the narration runs, instead of one static picture. Driven by
 * `progress` (0..1 through the stage audio), so the pictures and the voice
 * stay in step, and the people carry the emotion of each moment.
 *
 * Registry is keyed `${flowKey}:${stageOrder}`. A stage with no entry falls
 * back to its illustration, so these can be added one at a time.
 */
import React from 'react';

const INK = '#0f172a';
const INK_SOFT = '#334155';
const TEAL = '#0d9488';
const TEAL_MID = '#5eead4';
const TEAL_SOFT = '#ccfbf1';
const SAND = '#f5f0e6';
const AMBER = '#f59e0b';
const ROSE = '#fb7185';
const PAPER = '#ffffff';
const SKIN = '#e8b98d';
const SKIN_2 = '#c98a5b';

const clamp = (v) => Math.max(0, Math.min(1, v));

/* ---------------------------------------------------------------- faces */

// Eyebrows, eyes and mouth carry the mood. Moods:
// worried · hopeful · focused · smile · delight · surprised
const Face = ({ mood = 'smile', anim, blinkAt = 0 }) => {
  const brows = {
    worried: ['M-19 -14 q7 -5 13 -1', 'M19 -14 q-7 -5 -13 -1'],
    hopeful: ['M-19 -15 q7 -6 13 -2', 'M19 -15 q-7 -6 -13 -2'],
    focused: ['M-19 -12 q7 -2 13 -3', 'M19 -12 q-7 -2 -13 -3'],
    smile: ['M-19 -15 q7 -5 13 -2', 'M19 -15 q-7 -5 -13 -2'],
    delight: ['M-19 -17 q7 -7 13 -2', 'M19 -17 q-7 -7 -13 -2'],
    surprised: ['M-19 -18 q7 -6 13 -2', 'M19 -18 q-7 -6 -13 -2'],
  }[mood];

  const mouth = {
    worried: <path d="M-9 13 q9 -7 18 0" stroke={INK} strokeWidth="2.6" fill="none" strokeLinecap="round" />,
    hopeful: <path d="M-8 11 q8 4 16 -1" stroke={INK} strokeWidth="2.6" fill="none" strokeLinecap="round" />,
    focused: <path d="M-8 12 h16" stroke={INK} strokeWidth="2.6" fill="none" strokeLinecap="round" />,
    smile: <path d="M-10 9 q10 11 20 -1" stroke={INK} strokeWidth="2.8" fill="none" strokeLinecap="round" />,
    delight: (
      <g>
        <path d="M-12 8 q12 16 24 0 z" fill={INK} />
        <path d="M-6 16 q6 5 12 0" fill={ROSE} opacity="0.85" />
      </g>
    ),
    surprised: <ellipse cx="0" cy="13" rx="6" ry="7" fill={INK} />,
  }[mood];

  const happy = mood === 'delight' || mood === 'smile';

  return (
    <g>
      {brows.map((d, n) => (
        <path key={n} d={d} stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
      ))}
      {happy && mood === 'delight' ? (
        <>
          <path d="M-17 -2 q6 -8 12 0" stroke={INK} strokeWidth="3.4" fill="none" strokeLinecap="round" />
          <path d="M5 -2 q6 -8 12 0" stroke={INK} strokeWidth="3.4" fill="none" strokeLinecap="round" />
        </>
      ) : (
        <>
          <ellipse cx="-11" cy="-2" rx="3.4" ry="3.6" fill={INK}>
            {anim && (
              <animate attributeName="ry" values="3.6;3.6;0.4;3.6" keyTimes="0;0.93;0.96;1"
                dur="4.4s" begin={`${blinkAt}s`} repeatCount="indefinite" />
            )}
          </ellipse>
          <ellipse cx="11" cy="-2" rx="3.4" ry="3.6" fill={INK}>
            {anim && (
              <animate attributeName="ry" values="3.6;3.6;0.4;3.6" keyTimes="0;0.93;0.96;1"
                dur="4.4s" begin={`${blinkAt}s`} repeatCount="indefinite" />
            )}
          </ellipse>
        </>
      )}
      {mood === 'delight' && (
        <>
          <ellipse cx="-22" cy="8" rx="6" ry="4" fill={ROSE} opacity="0.35" />
          <ellipse cx="22" cy="8" rx="6" ry="4" fill={ROSE} opacity="0.35" />
        </>
      )}
      {mouth}
      {mood === 'worried' && (
        <g>
          <path d="M26 -6 q5 9 0 12 q-5 -3 0 -12" fill="#7dd3fc">
            {anim && <animate attributeName="opacity" values="0.2;1;0.2" dur="2.4s" repeatCount="indefinite" />}
          </path>
        </g>
      )}
    </g>
  );
};

// A person: head + torso + one expressive arm pose.
const Person = ({
  x, y, s = 1, shirt = TEAL, skin = SKIN, hair = INK,
  mood = 'smile', anim, arms = 'down', headset = false, cap = false,
  blinkAt = 0, bob = 0, legs = false, flip = false,
}) => (
  <g transform={`translate(${x} ${y}) scale(${flip ? -s : s} ${s})`}>
    <g>
      {anim && bob > 0 && (
        <animateTransform attributeName="transform" type="translate"
          values={`0 0; 0 -${bob}; 0 0`} dur="3.2s" repeatCount="indefinite" />
      )}
      <ellipse cx="0" cy={legs ? 168 : 124} rx={legs ? 40 : 46} ry="8" fill={INK} opacity="0.12" />
      {legs && (
        <g>
          <rect x="-23" y="112" width="18" height="48" rx="7" fill={INK_SOFT} />
          <rect x="5" y="112" width="18" height="48" rx="7" fill={INK_SOFT} />
          <rect x="-28" y="152" width="26" height="12" rx="6" fill={INK} />
          <rect x="2" y="152" width="26" height="12" rx="6" fill={INK} />
        </g>
      )}
      {/* neck */}
      <rect x="-11" y="22" width="22" height="20" rx="7" fill={skin} />
      {/* torso */}
      <path d="M-38 120 q-2 -64 38 -64 q40 0 38 64 z" fill={shirt} />
      <path d="M-38 120 q-2 -64 38 -64 q40 0 38 64 z" fill={PAPER} opacity="0.07" />
      <path d="M-22 100 h44" stroke={PAPER} strokeOpacity="0.26" strokeWidth="3" />
      <path d="M-7 58 l7 17 l7 -17" stroke={PAPER} strokeOpacity="0.4" strokeWidth="3" fill="none" />
      {/* arms */}
      {arms === 'down' && (
        <>
          <path d="M-32 66 q-15 24 -13 46" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="-45" cy="114" r="6.5" fill={skin} />
          <path d="M32 66 q15 24 13 46" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="45" cy="114" r="6.5" fill={skin} />
        </>
      )}
      {arms === 'phone' && (
        <>
          <path d="M-32 66 q-15 24 -13 46" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="-45" cy="114" r="6.5" fill={skin} />
          <path d="M30 66 q30 -6 26 -32" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <rect x="46" y="16" width="17" height="30" rx="5" fill={INK} transform="rotate(14 54 31)" />
          <rect x="49" y="20" width="11" height="20" rx="2" fill={TEAL_MID} transform="rotate(14 54 31)" opacity="0.8" />
        </>
      )}
      {arms === 'wave' && (
        <>
          <path d="M-32 66 q-15 24 -13 46" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="-45" cy="114" r="6.5" fill={skin} />
          <g>
            {anim && (
              <animateTransform attributeName="transform" type="rotate"
                values="-8 30 68; 12 30 68; -8 30 68" dur="1.6s" repeatCount="indefinite" />
            )}
            <path d="M30 68 q30 -12 26 -44" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
            <circle cx="56" cy="22" r="8" fill={skin} />
          </g>
        </>
      )}
      {arms === 'point' && (
        <>
          <path d="M-32 66 q-15 24 -13 46" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="-45" cy="114" r="6.5" fill={skin} />
          <path d="M28 70 q36 2 56 -14" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="86" cy="54" r="6.5" fill={skin} />
        </>
      )}
      {arms === 'up' && (
        <>
          <path d="M-30 68 q-28 -16 -24 -48" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <path d="M30 68 q28 -16 24 -48" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="-54" cy="18" r="7.5" fill={skin} />
          <circle cx="54" cy="18" r="7.5" fill={skin} />
        </>
      )}
      {arms === 'thumb' && (
        <>
          <path d="M-32 66 q-15 24 -13 46" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="-45" cy="114" r="6.5" fill={skin} />
          <path d="M28 70 q32 -4 36 -26" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="66" cy="42" r="9.5" fill={skin} />
          <rect x="62" y="22" width="8.5" height="18" rx="4.2" fill={skin} />
        </>
      )}
      {arms === 'tap' && (
        <>
          <path d="M-32 66 q-15 24 -13 46" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="-45" cy="114" r="6.5" fill={skin} />
          <path d="M28 70 q36 8 46 30" stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" />
          <circle cx="76" cy="102" r="7" fill={skin} />
        </>
      )}
      {/* head */}
      <circle cx="0" cy="0" r="31" fill={skin} />
      {cap ? (
        <>
          <path d="M-30 -8 q30 -36 60 0 z" fill={TEAL} />
          <path d="M-34 -8 h36 q-6 8 -36 6 z" fill={TEAL} opacity="0.8" />
        </>
      ) : (
        <path d="M-30 -6 q30 -36 60 0 q-7 -30 -30 -30 q-23 0 -30 30" fill={hair} />
      )}
      <Face mood={mood} anim={anim} blinkAt={blinkAt} />
      {headset && (
        <>
          <path d="M-31 -4 q31 -42 62 0" stroke={INK_SOFT} strokeWidth="6" fill="none" strokeLinecap="round" />
          <rect x="-38" y="-10" width="12" height="21" rx="6" fill={INK_SOFT} />
          <rect x="26" y="-10" width="12" height="21" rx="6" fill={INK_SOFT} />
          <path d="M28 11 q7 17 -12 19" stroke={INK_SOFT} strokeWidth="4" fill="none" strokeLinecap="round" />
          <circle cx="16" cy="30" r="4.5" fill={TEAL}>
            {anim && <animate attributeName="opacity" values="0.5;1;0.5" dur="1.4s" repeatCount="indefinite" />}
          </circle>
        </>
      )}
    </g>
  </g>
);

const Tick = ({ x, y, r = 9, color = TEAL }) => (
  <g>
    <circle cx={x} cy={y} r={r} fill={color} />
    <path
      d={`M${x - r * 0.45} ${y} l${r * 0.35} ${r * 0.38} l${r * 0.6} -${r * 0.72}`}
      stroke={PAPER} strokeWidth={r * 0.34} fill="none"
      strokeLinecap="round" strokeLinejoin="round"
    />
  </g>
);

const Cross = ({ x, y, r = 9 }) => (
  <g>
    <circle cx={x} cy={y} r={r} fill={ROSE} />
    <path d={`M${x - r * 0.4} ${y - r * 0.4} l${r * 0.8} ${r * 0.8} M${x + r * 0.4} ${y - r * 0.4} l-${r * 0.8} ${r * 0.8}`}
          stroke={PAPER} strokeWidth={r * 0.3} strokeLinecap="round" />
  </g>
);

const Sparkle = ({ x, y, s = 7, c = AMBER, dur = 2.2, begin = 0, anim }) => {
  const d = `M0 ${-s} L${s * 0.26} ${-s * 0.26} L${s} 0 L${s * 0.26} ${s * 0.26}`
    + ` L0 ${s} L${-s * 0.26} ${s * 0.26} L${-s} 0 L${-s * 0.26} ${-s * 0.26} Z`;
  return (
    <g transform={`translate(${x} ${y})`}>
      <path d={d} fill={c} opacity={anim ? 1 : 0.55}>
        {anim && (
          <>
            <animateTransform attributeName="transform" type="scale"
              values="0.15;1;0.15" dur={`${dur}s`} begin={`${begin}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;1;0" dur={`${dur}s`}
              begin={`${begin}s`} repeatCount="indefinite" />
          </>
        )}
      </path>
    </g>
  );
};

// Speech / thought bubble.
const Bubble = ({ x, y, w, h, tail = 'left', thought = false, children }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx={thought ? 20 : 14} fill={PAPER}
          stroke={INK} strokeOpacity="0.12" strokeWidth="2.5" />
    {thought ? (
      <>
        <circle cx={tail === 'left' ? x + 16 : x + w - 16} cy={y + h + 12} r="7" fill={PAPER}
                stroke={INK} strokeOpacity="0.12" strokeWidth="2.5" />
        <circle cx={tail === 'left' ? x + 4 : x + w - 4} cy={y + h + 26} r="4" fill={PAPER}
                stroke={INK} strokeOpacity="0.12" strokeWidth="2.5" />
      </>
    ) : (
      <path
        d={tail === 'left'
          ? `M${x + 22} ${y + h} l-14 18 l30 -18 z`
          : `M${x + w - 22} ${y + h} l14 18 l-30 -18 z`}
        fill={PAPER} stroke={INK} strokeOpacity="0.1" strokeWidth="2"
      />
    )}
    {children}
  </g>
);

const Dots = ({ x, y, anim, c = TEAL }) => (
  <g>
    {[0, 1, 2].map((d) => (
      <circle key={d} cx={x + d * 12} cy={y} r="4" fill={c} opacity="0.5">
        {anim && (
          <animate attributeName="opacity" values="0.2;1;0.2" dur="1.2s"
            begin={`${d * 0.2}s`} repeatCount="indefinite" />
        )}
      </circle>
    ))}
  </g>
);

// A small car, used in several scenes.
const Car = ({ x, y, s = 1, body = TEAL, spin = false, anim, ribbon = false }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <ellipse cx="0" cy="24" rx="66" ry="8" fill={INK} opacity="0.14" />
    <path d="M-56 14 l18 -28 h56 l20 28 z" fill={INK} />
    <path d="M-36 -12 h44 l13 20 h-66 z" fill={TEAL_MID} opacity="0.92" />
    <path d="M12 -12 h8 l10 20 h-16 z" fill={PAPER} opacity="0.35" />
    <rect x="-62" y="8" width="124" height="18" rx="8" fill={body} />
    <rect x="-56" y="11" width="46" height="5" rx="2.5" fill={PAPER} opacity="0.3" />
    <rect x="58" y="12" width="10" height="6" rx="3" fill={AMBER} />
    {[-34, 34].map((wx) => (
      <g key={wx}>
        <circle cx={wx} cy="26" r="9.5" fill={INK} />
        <circle cx={wx} cy="26" r="4" fill="#94a3b8" />
        {spin && anim && (
          <line x1={wx} y1="18" x2={wx} y2="34" stroke={PAPER} strokeWidth="1.8" opacity="0.7">
            <animateTransform attributeName="transform" type="rotate"
              values={`0 ${wx} 26; 360 ${wx} 26`} dur="0.7s" repeatCount="indefinite" />
          </line>
        )}
      </g>
    ))}
    {ribbon && (
      <g>
        <rect x="-6" y="-14" width="12" height="40" fill={AMBER} opacity="0.9" />
        <path d="M0 -14 q-18 -14 -22 0 q14 6 22 0 q8 6 22 0 q-4 -14 -22 0" fill={AMBER} />
      </g>
    )}
  </g>
);

/* --------------------------------------------------- scene 1 · enquiry */

const SceneEnquiry = ({ t, anim }) => {
  const send = clamp((t - 0.42) / 0.38);
  const mood = t < 0.45 ? 'worried' : 'hopeful';
  return (
    <g>
      <rect width="640" height="420" fill="#f7f1e4" />
      <rect y="318" width="640" height="102" fill="#e6dcc6" />
      <rect y="314" width="640" height="6" fill={INK} opacity="0.1" />
      {/* home wall: framed print, sofa, plant */}
      <rect x="242" y="46" width="86" height="64" rx="6" fill={PAPER} stroke={INK} strokeOpacity="0.14" strokeWidth="3" />
      <path d="M250 102 l24 -28 l17 19 l15 -13 l17 22 z" fill={TEAL_SOFT} />
      <circle cx="308" cy="64" r="7" fill={AMBER} opacity="0.8" />
      <g>
        <rect x="26" y="238" width="152" height="80" rx="14" fill="#d9cbb0" />
        <rect x="38" y="220" width="128" height="36" rx="12" fill="#e4d8c0" />
        <rect x="52" y="230" width="44" height="20" rx="8" fill={TEAL_SOFT} opacity="0.9" />
      </g>
      <g>
        <path d="M598 318 q-14 -40 0 -66 q14 28 0 66" fill={TEAL} opacity="0.8" />
        <path d="M598 318 q-30 -20 -34 -48 q28 12 34 48" fill={TEAL_MID} opacity="0.85" />
        <rect x="580" y="316" width="38" height="26" rx="5" fill={INK_SOFT} />
      </g>

      {/* his car outside the window, dull and waiting */}
      <g opacity="0.95">
        <rect x="32" y="56" width="188" height="118" rx="10" fill="#dbeeea" stroke={INK} strokeOpacity="0.14" strokeWidth="3" />
        <circle cx="64" cy="84" r="14" fill={PAPER} opacity="0.5" />
        <circle cx="86" cy="78" r="9" fill={PAPER} opacity="0.4" />
        <rect x="32" y="150" width="188" height="24" fill={INK} opacity="0.08" />
        <Car x={126} y={140} s={0.6} body="#8aa0a8" anim={anim} />
        {/* scratch on the paint, found under a loupe */}
        <g>
          <path d="M100 136 l22 -8" stroke={PAPER} strokeWidth="3" strokeLinecap="round" opacity="0.9" />
          <circle cx="112" cy="130" r="16" fill="none" stroke={INK_SOFT} strokeWidth="3.5" />
          <path d="M124 142 l12 12" stroke={INK_SOFT} strokeWidth="4.5" strokeLinecap="round" />
          {anim && (
            <circle cx="112" cy="130" r="16" fill={PAPER} opacity="0.18">
              <animate attributeName="opacity" values="0.05;0.3;0.05" dur="2.6s" repeatCount="indefinite" />
            </circle>
          )}
        </g>
      </g>

      {/* the customer, phone to his ear, unsure who to trust */}
      <Person x={274} y={146} s={1.0} shirt={AMBER} skin={SKIN_2} mood={mood}
              anim={anim} arms="phone" bob={3} blinkAt={0.4} legs />

      {/* three studios shopped the same day — chips fly away */}
      {[0, 1, 2].map((n) => {
        const f = clamp((send - n * 0.14) / 0.6);
        return (
          <g key={n} style={{
            opacity: f,
            transform: `translate(${f * (52 + n * 12)}px, ${-f * (18 + n * 14)}px)`,
            transition: 'opacity 400ms ease, transform 800ms cubic-bezier(.22,.7,.3,1)',
          }}>
            <rect x={358} y={158 + n * 40} width="96" height="28" rx="14" fill={PAPER}
                  stroke={INK} strokeOpacity="0.1" strokeWidth="2" />
            <circle cx={374} cy={172 + n * 40} r="8" fill={n === 0 ? TEAL : n === 1 ? '#22c55e' : AMBER} />
            {n === 0 && <path d="M370.5 169 q3.5 5 7 6" stroke={PAPER} strokeWidth="2.2" fill="none" strokeLinecap="round" />}
            {n === 1 && <path d="M370 216 v-5 a4.4 4.4 0 1 1 4.4 4.4 z" fill={PAPER} />}
            {n === 2 && (
              <g stroke={PAPER} strokeWidth="1.5" fill="none">
                <circle cx={374} cy={172 + n * 40} r="4.6" />
                <path d={`M369.4 ${172 + n * 40} h9.2 M374 ${167.4 + n * 40} q3.4 4.6 0 9.2 q-3.4 -4.6 0 -9.2`} />
              </g>
            )}
            <rect x={388} y={169 + n * 40} width="34" height="6" rx="3" fill={INK} opacity="0.28" />
            <Dots x={430} y={172 + n * 40} anim={anim} />
          </g>
        );
      })}

      {/* the clock is running on the answer */}
      <g>
        <circle cx="568" cy="106" r="30" fill={PAPER} stroke={AMBER} strokeWidth="4" />
        <circle cx="568" cy="106" r="2.6" fill={INK} />
        <line x1="568" y1="106" x2="568" y2="88" stroke={INK} strokeWidth="3" strokeLinecap="round">
          {anim && (
            <animateTransform attributeName="transform" type="rotate"
              values="0 568 106; 360 568 106" dur="2.4s" repeatCount="indefinite" />
          )}
        </line>
        <circle cx="568" cy="106" r="34" fill="none" stroke={AMBER} strokeWidth="3" opacity="0.5">
          {anim && (
            <>
              <animate attributeName="r" values="32;44" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.5;0" dur="2s" repeatCount="indefinite" />
            </>
          )}
        </circle>
      </g>
    </g>
  );
};

/* --------------------------------------------- scene 2 · we answer fast */

const SceneAnswer = ({ t, anim }) => {
  const picked = t > 0.3;
  const ring = clamp(t / 0.32);
  return (
    <g>
      <rect width="640" height="420" fill="#eef7f5" />
      <rect y="318" width="640" height="102" fill="#dceae7" />
      <rect y="314" width="640" height="6" fill={INK} opacity="0.1" />
      {/* front office wall */}
      <rect x="0" y="0" width="640" height="78" fill={TEAL} opacity="0.12" />
      {[110, 300, 490].map((x, n) => (
        <g key={x}>
          <line x1={x} y1="0" x2={x} y2="22" stroke={INK} strokeWidth="3" opacity="0.4" />
          <path d={`M${x - 18} 38 l18 -16 l18 16 z`} fill={INK} opacity="0.85" />
          <ellipse cx={x} cy="38" rx="18" ry="4.5" fill="#fde68a" />
          <path d={`M${x - 30} 40 h60 l18 66 h-96 z`} fill="#fde68a" opacity="0.28">
            {anim && <animate attributeName="opacity" values="0.18;0.34;0.18" dur={`${4 + n}s`} repeatCount="indefinite" />}
          </path>
        </g>
      ))}

      {/* the advisor answers on the first ring, warm and ready */}
      <Person x={190} y={176} s={1.08} shirt={TEAL} mood={picked ? 'smile' : 'surprised'}
              anim={anim} arms="wave" headset bob={3} />

      {/* desk with the workstation */}
      <rect x="26" y="300" width="588" height="15" rx="7" fill={INK} opacity="0.9" />
      <g>
        <rect x="296" y="212" width="120" height="80" rx="8" fill={INK} />
        <rect x="304" y="220" width="104" height="58" rx="4" fill="#0b3b39" />
        <rect x="340" y="292" width="26" height="9" fill={INK} />
        {[0, 1, 2, 3].map((r) => [0, 1, 2, 3, 4].map((c) => (
          <rect key={`${r}-${c}`} x={310 + c * 19} y={228 + r * 13} width={14} height={8} rx={2}
                fill={r === 1 && c === 2 ? TEAL_MID : TEAL} opacity={r === 1 && c === 2 ? 1 : 0.3}>
            {anim && r === 1 && c === 2 && (
              <animate attributeName="opacity" values="0.4;1;0.4" dur="1.6s" repeatCount="indefinite" />
            )}
          </rect>
        )))}
      </g>

      {/* the caller on the other end relaxes once we pick up */}
      <g>
        <circle cx="518" cy="176" r="58" fill={PAPER} opacity="0.9" />
        <circle cx="518" cy="176" r="58" fill="none" stroke={TEAL} strokeOpacity="0.2" strokeWidth="3" />
        <g transform="translate(518 186) scale(0.86)">
          <circle cx="0" cy="-14" r="28" fill={SKIN_2} />
          <path d="M-27 -20 q27 -34 54 0 q-6 -28 -27 -28 q-21 0 -27 28" fill={INK} />
          <g transform="translate(0 -14)">
            <Face mood={picked ? 'hopeful' : 'worried'} anim={anim} blinkAt={0.9} />
          </g>
          <path d="M-30 44 q30 -32 60 0 z" fill={AMBER} />
        </g>
        {anim && [0, 1].map((n) => (
          <circle key={n} cx="518" cy="176" r="58" fill="none" stroke={TEAL} strokeWidth="2.5" opacity="0.45">
            <animate attributeName="r" values="56;80" dur="2.6s" begin={`${n * 1.3}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.45;0" dur="2.6s" begin={`${n * 1.3}s`} repeatCount="indefinite" />
          </circle>
        ))}
      </g>

      {/* the greeting bubble */}
      <Bubble x={252} y={70} w={150} h={58} tail="left">
        <rect x={270} y={86} width={78} height={7} rx={3.5} fill={INK} opacity="0.28" />
        <rect x={270} y={100} width={52} height={7} rx={3.5} fill={TEAL} opacity="0.55" />
        {!picked && <Dots x={360} y={98} anim={anim} />}
        {picked && <Tick x={372} y={99} r={9} />}
      </Bubble>

      {/* answered inside the promised time */}
      <g transform="translate(66 108)">
        <circle cx="0" cy="0" r="34" fill={PAPER} />
        <circle cx="0" cy="0" r="30" fill="none" stroke={INK} strokeOpacity="0.1" strokeWidth="7" />
        <circle
          cx="0" cy="0" r="30" fill="none" stroke={picked ? TEAL : AMBER} strokeWidth="7"
          strokeLinecap="round" transform="rotate(-90)"
          strokeDasharray={2 * Math.PI * 30}
          strokeDashoffset={2 * Math.PI * 30 * (1 - ring)}
          style={{ transition: 'stroke-dashoffset 600ms linear, stroke 400ms ease' }}
        />
        <g style={{ opacity: picked ? 1 : 0, transition: 'opacity 400ms ease' }}>
          <Tick x={0} y={0} r={13} />
        </g>
        {!picked && (
          <g stroke={AMBER} strokeWidth="4" strokeLinecap="round" fill="none">
            <path d="M0 -12 v12 l9 6" stroke={INK} strokeOpacity="0.55" />
          </g>
        )}
      </g>
    </g>
  );
};

/* ------------------------------------------ scene 3 · the four questions */

const SceneQuestions = ({ t, anim }) => {
  const rows = t * 4.4;
  return (
    <g>
      <rect width="640" height="420" fill="#f4f1e9" />
      <rect y="318" width="640" height="102" fill="#e5e0d1" />
      <rect y="314" width="640" height="6" fill={INK} opacity="0.1" />
      <circle cx="98" cy="110" r="74" fill={TEAL_SOFT} opacity="0.5" />

      {/* the advisor, listening hard and writing it down */}
      <Person x={140} y={148} s={1.0} shirt={TEAL} mood="focused" anim={anim} arms="point" headset bob={2} legs />

      {/* what he is picturing: the car, the paint, the timeline */}
      <Bubble x={276} y={40} w={330} h={196} tail="left" thought>
        <rect x={296} y={58} width={96} height={9} rx={4.5} fill={TEAL} opacity="0.5" />
        {[
          { label: 'vehicle', icon: 'car' },
          { label: 'service', icon: 'shield' },
          { label: 'paint', icon: 'swatch' },
          { label: 'timeline', icon: 'clock' },
        ].map((q, idx) => {
          const on = rows > idx + 1;
          return (
            <g key={q.label} style={{
              opacity: rows > idx + 0.2 ? 1 : 0.25,
              transition: 'opacity 400ms ease',
            }}>
              <rect x={296} y={80 + idx * 36} width={290} height={28} rx={14}
                    fill={on ? TEAL_SOFT : PAPER} stroke={INK} strokeOpacity="0.1" strokeWidth="2"
                    style={{ transition: 'fill 420ms ease' }} />
              {/* icon */}
              <g transform={`translate(${314} ${94 + idx * 36})`}>
                {q.icon === 'car' && (
                  <g>
                    <path d="M-10 3 l3 -7 h11 l4 7 z" fill={INK} opacity="0.75" />
                    <circle cx="-5" cy="4" r="2.4" fill={INK} opacity="0.75" />
                    <circle cx="6" cy="4" r="2.4" fill={INK} opacity="0.75" />
                  </g>
                )}
                {q.icon === 'shield' && (
                  <path d="M0 -8 l9 4 v6 q0 7 -9 10 q-9 -3 -9 -10 v-6 z" fill={TEAL} opacity="0.8" />
                )}
                {q.icon === 'swatch' && (
                  <g>
                    <rect x="-10" y="-7" width="7" height="14" rx="2" fill={INK_SOFT} />
                    <rect x="-1" y="-7" width="7" height="14" rx="2" fill={TEAL} />
                    <rect x="8" y="-7" width="6" height="14" rx="2" fill={AMBER} />
                  </g>
                )}
                {q.icon === 'clock' && (
                  <g stroke={INK} strokeOpacity="0.7" strokeWidth="2" fill="none">
                    <circle cx="0" cy="0" r="8" />
                    <path d="M0 -5 v5 l4 3" strokeLinecap="round" />
                  </g>
                )}
              </g>
              <rect x={332} y={91 + idx * 36} width={idx % 2 ? 122 : 168} height={7} rx={3.5}
                    fill={INK} opacity={on ? 0.4 : 0.2} style={{ transition: 'opacity 400ms ease' }} />
              <g style={{
                opacity: on ? 1 : 0,
                transform: on ? 'none' : 'scale(0.3)',
                transformOrigin: `${566}px ${94 + idx * 36}px`,
                transition: 'opacity 300ms ease, transform 460ms cubic-bezier(.22,1.5,.4,1)',
              }}>
                <Tick x={566} y={94 + idx * 36} r={9} />
              </g>
            </g>
          );
        })}
      </Bubble>

      {/* the answer written on the job card */}
      <g transform="translate(300 268)">
        <rect x="0" y="0" width="190" height="46" rx="10" fill={PAPER} stroke={INK} strokeOpacity="0.12" strokeWidth="2.5" />
        <rect x="16" y="14" width="86" height="7" rx="3.5" fill={INK} opacity="0.3" />
        <rect x="16" y="28" width="54" height="7" rx="3.5" fill={TEAL} opacity="0.55" />
        <g style={{ opacity: rows > 4 ? 1 : 0, transition: 'opacity 400ms ease' }}>
          <Tick x={164} y={24} r={11} />
        </g>
      </g>
      <g transform="translate(524 276)">
        <rect x="0" y="0" width="76" height="34" rx="17" fill={INK} />
        <rect x="14" y="14" width="34" height="6" rx="3" fill={PAPER} opacity="0.8" />
        <Dots x={56} y={17} anim={anim} c={TEAL_MID} />
      </g>
    </g>
  );
};

/* ---------------------------------- scene 4 · honest slot + weekly cap */

const SceneSlot = ({ t, anim }) => {
  const fill = clamp(t / 0.42);
  const bays = Math.min(4, Math.round(clamp((t - 0.35) / 0.4) * 4));
  const full = bays >= 4;
  return (
    <g>
      <rect width="640" height="420" fill="#f1f5f4" />
      <rect y="330" width="640" height="90" fill="#dfe7e5" />
      <rect y="326" width="640" height="6" fill={INK} opacity="0.1" />

      {/* the advisor points at a slot he can actually deliver */}
      <Person x={88} y={158} s={0.92} shirt={TEAL} mood="focused" anim={anim} arms="point" headset bob={2} legs />

      {/* the calendar board */}
      <g>
        <rect x="196" y="40" width="272" height="176" rx="16" fill={PAPER} stroke={INK} strokeOpacity="0.12" strokeWidth="3" />
        <rect x="196" y="40" width="272" height="34" rx="16" fill={TEAL} opacity="0.14" />
        <rect x="216" y="52" width="88" height="10" rx="5" fill={TEAL} opacity="0.6" />
        {[0, 1, 2, 3, 4].map((c) => (
          <rect key={`h${c}`} x={216 + c * 48} y={86} width={34} height={9} rx={4.5} fill={INK} opacity="0.15" />
        ))}
        {[0, 1, 2].map((r) => [0, 1, 2, 3, 4].map((c) => (
          <rect key={`${r}-${c}`} x={216 + c * 48} y={106 + r * 34} width={34} height={26} rx={6}
                fill={INK} opacity="0.06" />
        )))}
        {/* two bay days get blocked, in sequence */}
        <rect x={312} y={140} width={34} height={26} rx={6} fill={TEAL}
              style={{ transform: `scaleY(${fill})`, transformOrigin: '329px 166px',
                transition: 'transform 700ms cubic-bezier(.22,.7,.3,1)' }} />
        <rect x={360} y={140} width={34} height={26} rx={6} fill={TEAL_MID}
              style={{ transform: `scaleY(${clamp((fill - 0.3) / 0.7)})`, transformOrigin: '377px 166px',
                transition: 'transform 700ms cubic-bezier(.22,.7,.3,1) 160ms' }} />
        <rect x={308} y={136} width={90} height={34} rx={8} fill="none" stroke={TEAL} strokeWidth="3"
              style={{ opacity: fill > 0.5 ? 1 : 0, transition: 'opacity 400ms ease' }}>
          {anim && <animate attributeName="stroke-opacity" values="0.35;1;0.35" dur="2s" repeatCount="indefinite" />}
        </rect>
        <g style={{ opacity: fill > 0.85 ? 1 : 0, transition: 'opacity 420ms ease' }}>
          <rect x="300" y="182" width="106" height="24" rx="12" fill={INK} />
          <Tick x={314} y={194} r={7} />
          <rect x="326" y="191" width="66" height="6" rx="3" fill={PAPER} opacity="0.85" />
        </g>
      </g>

      {/* a vague 'bring it anytime' is crossed out */}
      <g style={{ opacity: fill > 0.6 ? 1 : 0, transition: 'opacity 420ms ease' }}>
        <g transform="translate(534 96)">
          <circle cx="0" cy="0" r="34" fill={PAPER} stroke={INK} strokeOpacity="0.12" strokeWidth="3" />
          <path d="M0 -16 v16 l11 8" stroke={INK} strokeOpacity="0.4" strokeWidth="4"
                fill="none" strokeLinecap="round" />
          <Cross x={26} y={26} r={12} />
        </g>
        <g transform="translate(534 190)">
          <rect x="-34" y="-28" width="68" height="56" rx="10" fill={PAPER} stroke={INK} strokeOpacity="0.12" strokeWidth="3" />
          <rect x="-34" y="-28" width="68" height="14" rx="7" fill={TEAL} opacity="0.35" />
          <rect x="-20" y="0" width="40" height="8" rx="4" fill={INK} opacity="0.2" />
          <rect x="-20" y="14" width="24" height="8" rx="4" fill={TEAL} opacity="0.5" />
          <Tick x={26} y={26} r={12} />
        </g>
      </g>

      {/* the weekly PPF cap: four bays, and the honest 'no' when it is full */}
      <g>
        <rect x="196" y="240" width="272" height="76" rx="14" fill={PAPER} stroke={INK} strokeOpacity="0.12" strokeWidth="3" />
        <rect x="216" y="252" width="70" height="8" rx="4" fill={TEAL} opacity="0.55" />
        {[0, 1, 2, 3].map((n) => (
          <g key={n}>
            <rect x={214 + n * 62} y={270} width={50} height={32} rx={8}
                  fill={n < bays ? TEAL : PAPER}
                  stroke={n < bays ? TEAL : n === 3 ? AMBER : INK}
                  strokeOpacity={n === 3 && !full ? 1 : 0.22} strokeWidth="2.5"
                  style={{ transition: 'fill 420ms ease, stroke 420ms ease' }} />
            <g opacity={n < bays ? 0.95 : 0.28}>
              <path d={`M${224 + n * 62} 292 l6 -9 h18 l7 9 z`} fill={n < bays ? PAPER : INK} />
              <circle cx={230 + n * 62} cy={293} r={2.8} fill={n < bays ? PAPER : INK} />
              <circle cx={248 + n * 62} cy={293} r={2.8} fill={n < bays ? PAPER : INK} />
            </g>
          </g>
        ))}
        <g style={{ opacity: full ? 1 : 0, transition: 'opacity 420ms ease' }}>
          <circle cx="482" cy="250" r="14" fill={AMBER}>
            {anim && <animate attributeName="r" values="12;16;12" dur="1.7s" repeatCount="indefinite" />}
          </circle>
          <rect x="480.6" y="243" width="3" height="9" rx="1.5" fill={PAPER} />
          <circle cx="482" cy="256" r="2" fill={PAPER} />
          {/* the fifth job politely moves to next week */}
          <g transform="translate(500 292)">
            <path d="M0 0 h44" stroke={AMBER} strokeWidth="4" strokeLinecap="round" />
            <path d="M38 -7 l8 7 l-8 7" stroke={AMBER} strokeWidth="4" fill="none"
                  strokeLinecap="round" strokeLinejoin="round" />
            <rect x="52" y="-16" width="40" height="32" rx="8" fill={PAPER} stroke={AMBER} strokeWidth="2.5" />
            <rect x="60" y="-4" width="24" height="6" rx="3" fill={AMBER} opacity="0.7" />
          </g>
        </g>
      </g>
    </g>
  );
};

/* ------------------------------------------- scene 5 · advance collected */

const SceneAdvance = ({ t, anim }) => {
  const coin = clamp((t - 0.08) / 0.34);
  const paid = t > 0.5;
  const shake = t > 0.72;
  return (
    <g>
      <rect width="640" height="420" fill="#f6f2e8" />
      <rect y="318" width="640" height="102" fill="#e8e0cd" />
      {/* the counter */}
      <rect x="0" y="300" width="640" height="24" rx="6" fill={INK} opacity="0.9" />
      <rect x="12" y="304" width="240" height="5" rx="2.5" fill={PAPER} opacity="0.18" />
      <circle cx="320" cy="150" r="128" fill={TEAL_SOFT} opacity="0.4" />

      {/* both sides of the counter, pleased with each other */}
      <Person x={96} y={172} s={0.96} shirt={TEAL} mood={paid ? 'smile' : 'focused'}
              anim={anim} arms="tap" headset bob={2} />
      <Person x={550} y={172} s={0.96} shirt={AMBER} skin={SKIN_2} flip
              mood={paid ? 'delight' : 'hopeful'} anim={anim} arms="tap" bob={3} blinkAt={0.7} />

      {/* the card machine on the counter */}
      <g>
        <ellipse cx="322" cy="300" rx="66" ry="9" fill={INK} opacity="0.12" />
        <rect x="274" y="222" width="98" height="70" rx="12" fill={INK} />
        <rect x="284" y="236" width="78" height="32" rx="6" fill={PAPER} />
        <rect x="292" y="243" width="36" height="6" rx="3" fill={INK} opacity="0.3" />
        <rect x="292" y="254" width="24" height="6" rx="3" fill={TEAL} opacity="0.7" />
        {[0, 1, 2].map((r) => [0, 1, 2].map((c) => (
          <rect key={`${r}-${c}`} x={288 + c * 19} y={274 + r * 6} width={14} height={4} rx={2}
                fill={PAPER} opacity="0.25" />
        )))}
        {/* the card taps down */}
        <g style={{ opacity: coin > 0.02 && !paid ? 1 : 0, transition: 'opacity 300ms ease' }}>
          <rect x={296} y={168 + coin * 40} width={54} height={34} rx={6} fill={AMBER} />
          <rect x={302} y={176 + coin * 40} width={18} height={12} rx={3} fill={PAPER} opacity="0.55" />
          <rect x={302} y={192 + coin * 40} width={38} height={4} rx={2} fill={PAPER} opacity="0.35" />
        </g>
        {anim && coin > 0.6 && [0, 1].map((n) => (
          <circle key={n} cx="322" cy="204" r="10" fill="none" stroke={AMBER} strokeWidth="3" opacity="0.6">
            <animate attributeName="r" values="10;36" dur="1.6s" begin={`${n * 0.8}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;0" dur="1.6s" begin={`${n * 0.8}s`} repeatCount="indefinite" />
          </circle>
        ))}
        {/* approved */}
        <g style={{
          opacity: paid ? 1 : 0,
          transform: paid ? 'none' : 'translateY(14px) scale(0.6)',
          transformOrigin: '322px 176px',
          transition: 'opacity 340ms ease, transform 520ms cubic-bezier(.22,1.4,.4,1)',
        }}>
          <Tick x={322} y={176} r={22} />
        </g>
      </g>

      {/* the receipt printing, then stamped */}
      <g>
        <rect x="410" y="196" width="94" height={Math.round(22 + coin * 58)} rx="8"
              fill={PAPER} stroke={INK} strokeOpacity="0.14" strokeWidth="2.5"
              style={{ transition: 'height 620ms cubic-bezier(.22,.7,.3,1)' }} />
        <rect x="424" y="208" width="58" height="6" rx="3" fill={INK} opacity="0.32" />
        <rect x="424" y="220" width="40" height="6" rx="3" fill={INK} opacity="0.2" />
        <g style={{ opacity: coin > 0.55 ? 1 : 0, transition: 'opacity 380ms ease' }}>
          <rect x="424" y="232" width="50" height="6" rx="3" fill={INK} opacity="0.14" />
          <rect x="424" y="244" width="32" height="6" rx="3" fill={TEAL} opacity="0.6" />
        </g>
        <g style={{ opacity: coin > 0.8 ? 1 : 0, transition: 'opacity 300ms ease' }}>
          {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
            <path key={n} d={`M${412 + n * 11.6} 278 l5.8 5 l5.8 -5`} fill="#f6f2e8" />
          ))}
        </g>
        <g style={{
          opacity: paid ? 1 : 0,
          transform: paid ? 'rotate(-12deg)' : 'rotate(26deg) scale(1.7)',
          transformOrigin: '486px 256px',
          transition: 'opacity 300ms ease, transform 480ms cubic-bezier(.22,1.3,.4,1)',
        }}>
          <circle cx="486" cy="256" r="20" fill="none" stroke={TEAL} strokeWidth="3.5" opacity="0.85" />
          <Tick x={486} y={256} r={13} />
        </g>
      </g>

      {/* it becomes a commitment — the handshake */}
      <g style={{
        opacity: shake ? 1 : 0,
        transform: shake ? 'none' : 'translateY(12px)',
        transition: 'opacity 420ms ease, transform 560ms cubic-bezier(.22,.7,.3,1)',
      }}>
        <g transform="translate(200 314)">
          <path d="M-46 2 q22 -15 44 4" stroke={SKIN} strokeWidth="14" fill="none" strokeLinecap="round" />
          <path d="M46 6 q-22 -15 -44 -4" stroke={SKIN_2} strokeWidth="14" fill="none" strokeLinecap="round" />
          <circle cx="0" cy="3" r="12" fill={SKIN} />
          {anim && (
            <g>
              <Sparkle x={-38} y={-22} s={7} c={AMBER} dur={2} begin={0.2} anim={anim} />
              <Sparkle x={40} y={-18} s={6} c={TEAL_MID} dur={2.3} begin={0.8} anim={anim} />
            </g>
          )}
        </g>
      </g>
    </g>
  );
};

/* ------------------------------------------------ scene 6 · confirmed */

const SceneConfirmed = ({ t, anim }) => {
  const party = clamp((t - 0.12) / 0.35);
  const send = clamp(t / 0.4);
  return (
    <g>
      <rect width="640" height="420" fill="#eff7f4" />
      <rect y="318" width="640" height="102" fill="#dbe9e4" />
      <rect y="314" width="640" height="6" fill={INK} opacity="0.1" />
      <circle cx="320" cy="176" r="150" fill={TEAL_SOFT} opacity="0.55">
        {anim && <animate attributeName="r" values="146;156;146" dur="3.6s" repeatCount="indefinite" />}
      </circle>

      {/* the car waiting in its booked bay, ready to be worked on */}
      <g opacity="0.95">
        <rect x="196" y="110" width="248" height="132" rx="14" fill={PAPER} opacity="0.75" />
        {[0, 1].map((n) => (
          <line key={n} x1={196 + n * 248} y1="110" x2={196 + n * 248} y2="242"
                stroke={TEAL} strokeOpacity="0.35" strokeWidth="4" />
        ))}
        <Car x={320} y={200} s={0.92} body={TEAL} anim={anim} ribbon />
      </g>

      {/* the two of them, genuinely pleased */}
      <Person x={80} y={146} s={0.92} shirt={TEAL} mood="smile" anim={anim} arms="thumb" headset bob={3} legs />
      <Person x={558} y={144} s={0.94} shirt={AMBER} skin={SKIN_2} mood="delight"
              anim={anim} arms="up" bob={4} blinkAt={0.5} legs />

      {/* the confirmation message lands on his phone */}
      <g style={{ transform: `translate(0px, ${(1 - send) * 26}px)`, opacity: send,
        transition: 'transform 700ms cubic-bezier(.22,.7,.3,1), opacity 400ms ease' }}>
        <rect x="228" y="278" width="184" height="72" rx="16" fill={INK} />
        <path d="M248 350 l-12 18 l26 -12 z" fill={INK} />
        <circle cx="252" cy="300" r="11" fill={TEAL_MID} />
        <path d="M247 300 l3.6 4 l6.6 -7.6" stroke={INK} strokeWidth="2.6" fill="none"
              strokeLinecap="round" strokeLinejoin="round" />
        <rect x="272" y="290" width="118" height="7" rx="3.5" fill={PAPER} opacity="0.85" />
        <rect x="272" y="304" width="86" height="7" rx="3.5" fill={PAPER} opacity="0.5" />
        <rect x="252" y="322" width="60" height="7" rx="3.5" fill={TEAL_MID} opacity="0.75" />
        <rect x="320" y="322" width="44" height="7" rx="3.5" fill={PAPER} opacity="0.35" />
      </g>

      {/* the stamp of a booking that will actually happen */}
      <g style={{
        opacity: party,
        transform: party > 0.1 ? 'none' : 'scale(0.5)',
        transformOrigin: '320px 176px',
        transition: 'opacity 400ms ease, transform 620ms cubic-bezier(.22,1.4,.4,1)',
      }}>
        {anim && [0, 1].map((n) => (
          <circle key={n} cx="320" cy="80" r="30" fill="none" stroke={TEAL} strokeWidth="3" opacity="0.5">
            <animate attributeName="r" values="28;62" dur="2.4s" begin={`${n * 1.2}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;0" dur="2.4s" begin={`${n * 1.2}s`} repeatCount="indefinite" />
          </circle>
        ))}
        <circle cx="320" cy="80" r="32" fill={TEAL}>
          {anim && <animate attributeName="r" values="30;34;30" dur="2.2s" repeatCount="indefinite" />}
        </circle>
        <path d="M305 80 l11 12 21 -24" stroke={PAPER} strokeWidth="7.5" fill="none"
              strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* confetti, streamers, sparkles */}
      {[[-118, -30, AMBER], [116, -36, TEAL_MID], [-146, 26, TEAL_MID], [140, 20, AMBER],
        [-42, -84, TEAL_MID], [54, -80, AMBER], [-92, 70, AMBER], [96, 74, TEAL],
        [-176, -8, TEAL], [172, -12, TEAL_MID], [-14, -104, ROSE], [26, 96, ROSE]].map(([dx, dy, c], n) => (
        <rect
          key={n} x={318 + dx} y={150 + dy} width={n % 3 === 0 ? 13 : 9} height={n % 3 === 0 ? 6 : 11}
          rx="2" fill={c}
          style={{
            opacity: party,
            transform: `translate(${dx * party * 0.4}px, ${dy * party * 0.4}px) rotate(${party * 280 + n * 31}deg)`,
            transformOrigin: 'center',
            transition: 'opacity 400ms ease, transform 950ms cubic-bezier(.22,.7,.3,1)',
          }}
        />
      ))}
      {[[-206, -58], [206, -66]].map(([dx, dy], n) => (
        <path key={n}
              d={`M${320 + dx} ${150 + dy} q12 -20 24 -4 q12 16 24 -4`}
              stroke={n ? AMBER : TEAL_MID} strokeWidth="4" fill="none" strokeLinecap="round"
              style={{ opacity: party * 0.9, transition: 'opacity 500ms ease' }} />
      ))}
      <g style={{ opacity: party }}>
        <Sparkle x={218} y={62} s={10} c={AMBER} dur={1.9} begin={0} anim={anim} />
        <Sparkle x={424} y={72} s={8} c={TEAL_MID} dur={2.3} begin={0.5} anim={anim} />
        <Sparkle x={168} y={140} s={7} c={TEAL} dur={2.1} begin={0.9} anim={anim} />
        <Sparkle x={472} y={148} s={9} c={AMBER} dur={2.5} begin={1.3} anim={anim} />
      </g>
    </g>
  );
};

/* ------------------------------------------------------- the storyboard */

const SCENES = [
  { at: 0.00, to: 0.17, Comp: SceneEnquiry },
  { at: 0.17, to: 0.33, Comp: SceneAnswer },
  { at: 0.33, to: 0.50, Comp: SceneQuestions },
  { at: 0.50, to: 0.68, Comp: SceneSlot },
  { at: 0.68, to: 0.86, Comp: SceneAdvance },
  { at: 0.86, to: 1.01, Comp: SceneConfirmed },
];

/** Studio · stage 1 — Enquiry & booking, told as six cut scenes. */
const EnquiryAndBooking = ({ progress = 0, playing = false }) => {
  const p = clamp(progress);
  const cur = Math.max(0, SCENES.findIndex((s) => p < s.to));
  const anim = playing;

  return (
    <svg viewBox="0 0 640 420" className="h-full w-full" role="img" aria-label="Enquiry and booking">
      <defs>
        <clipPath id="eb-frame"><rect width="640" height="420" /></clipPath>
        <radialGradient id="eb-vignette" cx="0.5" cy="0.45" r="0.75">
          <stop offset="0.6" stopColor="#000000" stopOpacity="0" />
          <stop offset="1" stopColor="#3b2f1c" stopOpacity="0.2" />
        </radialGradient>
        <linearGradient id="eb-sweep" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect width="640" height="420" fill={SAND} />
      <g clipPath="url(#eb-frame)">
        {SCENES.map(({ at, to, Comp }, n) => {
          if (Math.abs(n - cur) > 1) return null;
          const active = n === cur;
          const t = clamp((p - at) / (to - at));
          return (
            <g
              key={n}
              style={{
                opacity: active ? 1 : 0,
                transform: active ? 'none' : `translateX(${n < cur ? -52 : 52}px) scale(0.96)`,
                transformOrigin: 'center',
                transition: 'opacity 460ms ease, transform 640ms cubic-bezier(.22,.7,.3,1)',
              }}
            >
              <Comp t={t} anim={active && anim} />
            </g>
          );
        })}
      </g>

      {/* which scene of the story we are in */}
      <g>
        {SCENES.map((s, n) => (
          <rect key={n} x={252 + n * 24} y={404} width={n === cur ? 18 : 12} height={5} rx={2.5}
                fill={n === cur ? TEAL : INK}
                opacity={n === cur ? 0.9 : n < cur ? 0.35 : 0.15}
                style={{ transition: 'width 400ms ease, opacity 400ms ease, fill 400ms ease' }} />
        ))}
      </g>

      {anim && (
        <g clipPath="url(#eb-frame)" opacity="0.28" pointerEvents="none">
          <g transform="rotate(14 320 210)">
            <rect x="-300" y="-70" width="110" height="560" fill="url(#eb-sweep)">
              <animateTransform attributeName="transform" type="translate"
                values="0 0; 1120 0" dur="9s" repeatCount="indefinite" />
            </rect>
          </g>
        </g>
      )}
      <rect width="640" height="420" fill="url(#eb-vignette)" pointerEvents="none" />
    </svg>
  );
};

export const STAGE_ANIMATIONS = {
  'studio:1': EnquiryAndBooking,
};

export default STAGE_ANIMATIONS;
